import axios from "axios";
import { useEffect, useMemo, useState, useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { useSubmissionLock } from "../hooks/useSubmissionLock";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5001";

export default function TenantSignup() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { setUser } = useContext(AuthContext);

  const token = useMemo(() => params.get("token") || "", [params]);

  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [contactName, setContactName] = useState("");
  const [email, setEmail] = useState("");

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const { run: runSubmission, isPending } = useSubmissionLock();

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      setError("");

      try {
        const res = await axios.get(`${API_BASE}/api/tenant-invites/verify`, {
          params: { token },
          withCredentials: true,
        });

        if (res.data?.valid) {
          setValid(true);
          setEmail(res.data.email || "");
          setContactName(res.data.contactName || "");
        } else {
          setValid(false);
          setError(res.data?.error || "Invalid invite link.");
        }
      } catch (e) {
        setValid(false);
        setError(e?.response?.data?.error || "Invalid or expired invite link.");
      } finally {
        setLoading(false);
      }
    };

    if (token) run();
    else {
      setLoading(false);
      setValid(false);
      setError("Missing invite token.");
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting || isPending("tenant-signup")) return;

    setError("");

    if (!password || password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }

    await runSubmission("tenant-signup", async () => {
      try {
        setSubmitting(true);

        const res = await axios.post(
          `${API_BASE}/api/tenant-invites/accept`,
          { token, password },
          { withCredentials: true }
        );

        if (res.data?.token) {
          localStorage.setItem("accessToken", res.data.token);
        }

        if (res.data?.user) {
          setUser(res.data.user);
          navigate("/tenant-portal");
          return;
        }

        setError("Signup failed. Please try again.");
      } catch (e) {
        setError(e?.response?.data?.error || "Signup failed. Please try again.");
      } finally {
        setSubmitting(false);
      }
    });
  };

  return (
    <div className="flex h-screen">
      {/* LEFT: Signup Form */}
      <div className="w-full md:w-1/2 flex items-center justify-center bg-gradient-to-br from-[#111] via-[#1a1a1a] to-[#2b2b2b] p-8">
        <div className="bg-white/10 backdrop-blur-md border border-white/10 rounded-2xl p-10 w-full max-w-md shadow-2xl">
          <div className="text-center mb-10">
            <img
              src="https://cdn.prod.website-files.com/638fda8f8e2f041af84cb5a7/6824d581ae24f69d45e26536_beem%20logo%203.png"
              alt="Beem Logo"
              className="w-24 mx-auto mb-4"
            />
            <h2 className="text-3xl font-bold text-white mb-2">
              {loading ? "Checking link…" : valid ? "Create your password" : "Link problem"}
            </h2>
            <p className="text-gray-400 text-sm">
              {valid
                ? `Welcome${contactName ? `, ${contactName}` : ""}. Set a password to access your tenant portal.`
                : "This invite link is invalid or has expired."}
            </p>
            {valid && email ? (
              <p className="text-gray-500 text-xs mt-2">
                Signing up as <span className="text-gray-300">{email}</span>
              </p>
            ) : null}
          </div>

          {loading ? (
            <div className="text-gray-300">Loading…</div>
          ) : !valid ? (
            <div className="text-red-300">
              {error || "Invalid invite link."}
            </div>
          ) : (
            <>
              {error ? <div className="text-red-300 mb-4">{error}</div> : null}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="password" className="block mb-1 text-sm font-medium text-gray-300">
                    Password
                  </label>
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="w-full px-4 py-3 rounded-lg bg-white/15 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A]"
                    placeholder="••••••••"
                  />
                </div>

                <div>
                  <label htmlFor="confirm" className="block mb-1 text-sm font-medium text-gray-300">
                    Confirm password
                  </label>
                  <input
                    type="password"
                    id="confirm"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    className="w-full px-4 py-3 rounded-lg bg-white/15 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A]"
                    placeholder="••••••••"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full mt-4 bg-[#AF9B8A] hover:bg-[#9e8a78] disabled:opacity-60 text-white font-semibold py-3 rounded-lg transition-colors"
                >
                  {submitting ? "Creating account…" : "Create account"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>

      {/* RIGHT: Illustration */}
      <div className="hidden md:block w-1/2 relative">
        <img
          src="https://cdn.prod.website-files.com/638fda8f8e2f041af84cb5a7/68e10ee19b260e7f43fddd20_ChatGPT%20Image%20Oct%204%2C%202025%2C%2012_26_41%20PM.png"
          alt="City illustration"
          className="object-cover w-full h-full"
        />
        <div className="absolute inset-0 bg-gradient-to-l from-black/20 to-transparent" />
      </div>
    </div>
  );
}
